import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output } from "@angular/core";
import { LucideDynamicIcon } from "@lucide/angular";
import { cn } from "@/lib/utils";

const ISLAND_IMAGES: Record<string, string> = {
  "welcome-cove": "assets/islands/welcome-cove.png",
  "welcome-cove-ii": "assets/islands/welcome-cove.png",
  "prompt-peninsula": "assets/islands/prompt-peninsula.png",
  "automation-atoll": "assets/islands/automation-atoll.png",
  "ethics-isle": "assets/islands/ethics-isle.png",
  "collaboration-coral": "assets/islands/collaboration-coral.png",
  "future-frontier": "assets/islands/future-frontier.png",
};

@Component({
  selector: "app-visual-island",
  standalone: true,
  imports: [CommonModule, LucideDynamicIcon],
  template: `
    <div
      data-island="true"
      class="absolute z-40 animate-in zoom-in-95 fade-in"
      [style.left.%]="position.x"
      [style.top.%]="position.y"
      (mousedown)="islandMouseDown.emit($event)"
    >
      <div class="-translate-x-1/2 -translate-y-1/2">
        <button
          type="button"
          (click)="handleClick($event)"
          [attr.aria-disabled]="isLocked"
          [attr.aria-label]="name + '. ' + statusLabel + '.'"
          [attr.title]="titleLabel"
          [class]="buttonClass"
        >
          <span
            *ngIf="isCurrent && !isCompleted"
            class="absolute top-0 h-16 w-16 rounded-full bg-primary/30 blur-xl animate-pulse md:h-20 md:w-20"
          ></span>

          <span [class]="markerClass">
            <img
              [src]="islandImage"
              alt=""
              aria-hidden="true"
              class="absolute inset-0 h-full w-full object-cover opacity-25"
              [class.grayscale]="isLocked"
            />
            <span
              class="absolute inset-0 bg-gradient-to-br from-white/45 via-transparent to-black/15"
            ></span>
            <span
              class="relative text-lg font-black tracking-normal md:text-xl"
              >{{ levelNumber || delay + 1 }}</span
            >

            <span
              *ngIf="isLocked"
              class="absolute inset-0 grid place-items-center bg-slate-900/20"
            >
              <svg lucideIcon="lock" class="h-5 w-5"></svg>
            </span>
            <span
              *ngIf="isCompleted"
              class="absolute -right-1 -top-1 rounded-full bg-white p-0.5 text-emerald-600 shadow"
            >
              <svg
                lucideIcon="circle-check-big"
                class="h-4 w-4 fill-white"
              ></svg>
            </span>
            <span
              *ngIf="isCurrent && !isCompleted"
              class="absolute -right-1 -top-1 rounded-full bg-white p-0.5 text-primary shadow animate-pulse"
            >
              <svg lucideIcon="play" class="h-4 w-4 fill-primary"></svg>
            </span>
          </span>

          <span [class]="labelClass">{{ name }}</span>
        </button>
      </div>
    </div>
  `,
})
export class VisualIslandComponent {
  @Input() name = "";
  @Input() slug = "";
  @Input() imageUrl?: string | null;
  @Input() isLocked = false;
  @Input() isCompleted = false;
  @Input() isCurrent = false;
  @Input() position: { x: number; y: number } = { x: 50, y: 50 };
  @Input() delay = 0;
  @Input() levelNumber?: number;
  @Input() dragTitle = "Drag to reposition";
  @Input() isBeingMoved = false;
  @Output() islandClick = new EventEmitter<void>();
  @Output() islandMouseDown = new EventEmitter<MouseEvent>();

  get islandImage() {
    return (
      this.imageUrl || ISLAND_IMAGES[this.slug] || ISLAND_IMAGES["welcome-cove"]
    );
  }

  get statusLabel() {
    return this.isCompleted
      ? "Completed"
      : this.isCurrent
        ? "Current level"
        : this.isLocked
          ? "Locked"
          : "Available";
  }

  get titleLabel() {
    return `${this.name}. ${this.statusLabel}. ${this.dragTitle}`;
  }

  get buttonClass() {
    return cn(
      "group relative flex w-32 flex-col items-center text-center transition-transform md:w-40",
      this.isLocked
        ? "cursor-grab active:cursor-grabbing"
        : "cursor-grab hover:-translate-y-1 hover:scale-105 active:cursor-grabbing active:scale-95",
      this.isBeingMoved && "scale-105",
    );
  }

  get markerClass() {
    return cn(
      "relative grid h-14 w-14 place-items-center overflow-hidden rounded-full border-[3px] shadow-xl md:h-16 md:w-16",
      this.isCompleted && "border-emerald-200 bg-emerald-500 text-white",
      this.isCurrent &&
        !this.isCompleted &&
        "border-white bg-primary text-primary-foreground",
      !this.isCompleted &&
        !this.isCurrent &&
        !this.isLocked &&
        "border-white bg-amber-100 text-amber-950",
      this.isLocked &&
        "border-slate-200 bg-slate-300 text-slate-500 opacity-85",
    );
  }

  get labelClass() {
    return cn(
      "mt-2 max-w-[9rem] rounded-full border px-3 py-1 text-[11px] font-bold leading-tight shadow-lg backdrop-blur-sm md:max-w-[10rem] md:text-xs",
      this.isCompleted && "border-emerald-200 bg-emerald-50 text-emerald-800",
      this.isCurrent &&
        !this.isCompleted &&
        "border-primary/30 bg-white text-primary",
      !this.isCompleted &&
        !this.isCurrent &&
        !this.isLocked &&
        "border-amber-200 bg-white/95 text-amber-950",
      this.isLocked && "border-slate-200 bg-white/80 text-slate-500",
    );
  }

  handleClick(event: MouseEvent) {
    if (this.isLocked) {
      event.preventDefault();
      return;
    }

    this.islandClick.emit();
  }
}
